import { deletePendingOp, enqueueOp, listPendingOps, markPendingOpFailed } from './db';

function isNetworkError(err) {
  // axios: no response => network error / CORS / offline
  return !!err && !err.response;
}

function sleep(ms) {
  return new Promise((r) => setTimeout(r, ms));
}

const LOG = (_msg, _data) => {};

const MAX_RETRIES = 5;

function serializeBody(body) {
  if (body === null || body === undefined) return undefined;
  if (typeof body === 'string') return body;
  return JSON.stringify(body);
}

export async function flushPendingOps({ maxOps = 50 } = {}) {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    LOG('flush skipped: offline');
    return { ok: false, reason: 'offline' };
  }

  const ops = await listPendingOps(maxOps);
  LOG('flush start', { opsCount: ops.length, maxOps });
  let sent = 0;
  let failed = 0;

  const fetchFn =
    (typeof window !== 'undefined' && window.__madOriginalFetch) ||
    (typeof globalThis !== 'undefined' && globalThis.__madOriginalFetch) ||
    fetch;

  for (const op of ops) {
    if ((op.retryCount || 0) >= MAX_RETRIES) {
      LOG('max retries exceeded, removing', { opId: op.id, retries: op.retryCount });
      await deletePendingOp(op.id);
      failed += 1;
      continue;
    }

    LOG('replay', { id: op.id, method: op.method, url: op.url, bodyPreview: op.body ? JSON.stringify(op.body).slice(0, 200) : null });
    try {
      const headers = { ...(op.headers || {}) };
      const hasBody = op.body !== null && op.body !== undefined;
      const hasContentType =
        typeof headers['Content-Type'] === 'string' ||
        typeof headers['content-type'] === 'string';
      if (hasBody && !hasContentType) {
        headers['Content-Type'] = 'application/json';
      }

      const resp = await fetchFn(op.url, {
        method: op.method,
        headers,
        credentials: 'include',
        body: serializeBody(op.body),
      });

      // If auth expired or server rejects, stop and keep queue.
      if (resp.status === 401 || resp.status === 403) {
        LOG('auth error, stop flush', { status: resp.status });
        await markPendingOpFailed(op.id, `auth ${resp.status}`);
        return { ok: false, sent, failed, reason: 'auth' };
      }

      if (!resp.ok) {
        const ct = resp.headers.get('content-type') || '';
        LOG('error response', { status: resp.status, contentType: ct, url: op.url });
        let respBody = '';
        let respJson = null;
        try {
          const ctLow = (resp.headers.get('content-type') || '').toLowerCase();
          const text = await resp.text();
          if (ctLow.includes('text/html')) {
            LOG('response is HTML (not JSON)', { status: resp.status, preview: text.slice(0, 150) });
          }
          if (ctLow.includes('application/json')) {
            try {
              respJson = JSON.parse(text);
            } catch {
              respBody = text;
            }
          } else {
            respBody = text;
          }
        } catch {
          respBody = '';
        }

        const isPermanentFailure =
          resp.status === 400 ||
          resp.status === 404 ||
          resp.status === 422;
        if (isPermanentFailure) {
          LOG('permanent failure, remove from queue', { opId: op.id, status: resp.status, type: respJson?.type });
          await deletePendingOp(op.id);
          sent += 1;
        } else {
          LOG('retryable failure', { opId: op.id, status: resp.status, error: respJson?.error || respBody?.slice(0, 100) });
          failed += 1;
          const errMsg = respBody || (respJson ? JSON.stringify(respJson) : '');
          await markPendingOpFailed(op.id, errMsg ? `http ${resp.status}: ${errMsg}` : `http ${resp.status}`);
          await sleep(250);
        }
        continue;
      }

      LOG('success', { opId: op.id });
      await deletePendingOp(op.id);
      sent += 1;
    } catch (e) {
      LOG('replay exception', { opId: op.id, error: e?.message || e, url: op.url });
      failed += 1;
      await markPendingOpFailed(op.id, e?.message || e);
      if (isNetworkError(e)) {
        return { ok: false, sent, failed, reason: 'network' };
      }
      await sleep(250);
    }
  }

  LOG('flush done', { sent, failed, ok: failed === 0 });
  return { ok: failed === 0, sent, failed };
}

export async function queueOpFromAxiosConfig(config) {
  const method = String(config.method || 'get').toUpperCase();
  const baseURL = config.baseURL || '';
  const url = config.url || '';
  const fullUrl = `${baseURL}${url}`;

  const headers = { ...(config.headers || {}) };
  // Ensure JSON
  if (!headers['Content-Type'] && !headers['content-type']) {
    headers['Content-Type'] = 'application/json';
  }

  let body = config.data ?? null;
  if (typeof body === 'string') {
    try { body = JSON.parse(body); } catch { /* keep as string */ }
  }
  LOG('queue op', { method, url: fullUrl, bodyPreview: body ? JSON.stringify(body).slice(0, 150) : null });
  await enqueueOp({ method, url: fullUrl, headers, body });
}

